import {
  Document,
  Image,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  formatAmount,
  formatDate,
  stringToUpperCase,
} from "../../../../components/Helper";
import { SelfDocuments } from "../../../../components/Enums";

const Checkbox = ({ label, checked }: { label: string; checked: boolean }) => (
  <View
    style={{
      flexDirection: "row",
      alignItems: "center",
    }}
  >
    <View
      style={{
        width: 10,
        height: 10,
        border: 1,
        justifyContent: "center",
        alignItems: "center",
        marginRight: 5,
        fontSize: 6,
      }}
    >
      {checked && (
        <Svg viewBox="0 0 16 16" width={10} height={10}>
          <Path
            d="M2 8 L6 12 L14 2"
            stroke="black"
            strokeWidth={1}
            fill="none"
          />
        </Svg>
      )}
    </View>
    <Text>{label}</Text>
  </View>
);

const fontStyles = StyleSheet.create({
  TextCenter: {
    textAlign: "center",
  },
  boldText: {
    fontWeight: "bold",
  },
  fontXL: {
    fontSize: 12,
  },
  fontLg: {
    fontSize: 10,
  },
  fontMd: {
    fontSize: 9,
  },
  fontSm: { fontSize: 7.4 },
});

export const styles = StyleSheet.create({
  ...fontStyles,
  page: {
    border: "2px solid black",
    paddingHorizontal: 30,
    paddingVertical: 15,
    fontFamily: "HostGrotesk",
    backgroundColor: "white",
    borderTop: 1,
    ...fontStyles.fontMd,
  },
  header: {
    marginTop: 5,
    marginBottom: 10,
    ...fontStyles.fontXL,
  },
  table: {
    borderRight: 1,
    borderTop: 1,
    borderLeft: 1,
    width: "100%",
  },
  tableRow: {
    width: "100%",
    flexDirection: "row",
    borderBottom: 1,
  },
  tableCell: {
    paddingHorizontal: 3,
    borderRight: 1,
    flexDirection: "row",
  },
  flex: { flexDirection: "row" },
  centerDiv: {
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  checkbox: {
    width: 10,
    aspectRatio: 1,
    marginRight: 5,
    border: 1,
    fontSize: 6,
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    flexGrow: 1,
  },
  logo: {
    height: 100,
    width: 100,
    border: 1,
  },
});

const staticData = {
  DESCRIPTION_OF_COMMODITY_CONSIGNMENT: [
    { label: "Non GMO", checked: true },
    { label: "GMO: the CoA's reference no & date", checked: false },
  ],
  EXPORT_PURPOSE: "Consumption",
  DEGREE_OF_PROCESSING: [
    { label: "Fresh", checked: true },
    { label: "Minimal processed", checked: false },
    { label: "Full processed", checked: false },
    { label: "Other", checked: false },
  ],
};

const PriorNoticeInvoice = ({
  salesInvoiceData,
  settingsData,
  includeSignature,
}: any) => {
  const maxRows = 4;
  const productRowHeight = 40;

  const emptyRowHeight =
    salesInvoiceData.sales_invoice_products.length < maxRows
      ? (maxRows - salesInvoiceData.sales_invoice_products.length) *
        productRowHeight
      : 0;

  // const pageHeight = 860;
  // const hederHeight = 300;
  // const footerHeight = 280;
  // const productTableHeight =
  //   productDataLength <= 4 ? pageHeight - (hederHeight + footerHeight) : "auto";

  const documentInputFields =
    salesInvoiceData?.my_documents_inputs?.[SelfDocuments.PRIOR_NOTICE];

  return (
    <Document>
      <Page style={styles.page} size="A4">
        <View style={[styles.table, { maxHeight: 310 }]}>
          {/* Header */}
          <View
            style={[
              styles.centerDiv,
              { borderBottom: 1, borderRight: 1, paddingVertical: 10 },
            ]}
          >
            <Text style={[styles.fontLg, styles.boldText]}>PRIOR NOTICE</Text>
            <Text>
              Reference Number{" "}
              {stringToUpperCase(documentInputFields?.main_reference_no)}
            </Text>
          </View>

          {/* Importer-Exporter details */}
          <View>
            <View style={[styles.tableRow, styles.tableCell]}>
              <Text style={[styles.boldText]}>COUNTRY OF ORIGIN : </Text>
              <Text>INDIA</Text>
            </View>
            <View style={[styles.tableRow, styles.tableCell]}>
              <Text style={[styles.boldText]}>COUNTRY OF EXPORT : </Text>
              <Text>INDIA</Text>
            </View>

            {/* Exporter description */}
            <View style={[styles.tableRow, styles.boldText]}>
              <View
                style={[
                  styles.tableCell,
                  styles.centerDiv,
                  styles.fontLg,
                  { width: "25%" },
                ]}
              >
                <Text>Description of Exporter/Representative*</Text>
              </View>
              <View style={{ width: "75%" }}>
                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>Name</Text>
                  <Text style={[styles.tableCell, { width: "25%" }]}>
                    {settingsData?.name_of_proprietor}
                  </Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Company Name
                  </Text>
                  <Text style={[styles.tableCell, { width: "35%" }]}>
                    {settingsData?.company_name}
                  </Text>
                </View>

                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Address
                  </Text>
                  <Text style={[styles.tableCell, { width: "80%" }]}>
                    {settingsData?.company_address}
                  </Text>
                </View>

                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "45%" }]}>
                    Phone/Fax. Number
                  </Text>
                  <Text style={[styles.tableCell, { width: "55%" }]}>
                    {settingsData?.company_phone_1
                      ? settingsData?.company_phone_1
                      : settingsData?.company_phone_2 || ""}
                  </Text>
                </View>

                <View style={[styles.tableRow, { borderBottom: 0 }]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Email
                  </Text>
                  <Text style={[styles.tableCell, { width: "80%" }]}>
                    {settingsData?.company_email}
                  </Text>
                </View>
              </View>
            </View>

            {/* importer description */}
            <View style={[styles.tableRow, styles.boldText]}>
              <View
                style={[
                  styles.tableCell,
                  styles.centerDiv,
                  styles.fontLg,
                  { width: "25%" },
                ]}
              >
                <Text>Description of Importer/Representative*</Text>
              </View>
              <View style={{ width: "75%" }}>
                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>Name</Text>
                  <Text style={[styles.tableCell, { width: "25%" }]}>
                    {salesInvoiceData?.buyer?.name}
                  </Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Company name
                  </Text>
                  <Text style={[styles.tableCell, { width: "35%" }]}>
                    {salesInvoiceData?.buyer?.name}
                  </Text>
                </View>

                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Address
                  </Text>
                  <Text style={[styles.tableCell, { width: "80%" }]}>
                    {salesInvoiceData?.buyer?.address}
                  </Text>
                </View>

                <View style={[styles.tableRow]}>
                  <Text style={[styles.tableCell, { width: "45%" }]}>
                    Phone/Fax. Number
                  </Text>
                  <Text style={[styles.tableCell, { width: "55%" }]}>
                    {salesInvoiceData?.buyer?.phone_no}
                  </Text>
                </View>

                <View style={[styles.tableRow, { borderBottom: 0 }]}>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Email
                  </Text>
                  <Text style={[styles.tableCell, { width: "80%" }]}>
                    {salesInvoiceData?.buyer?.email}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* DESCRIPTION OF COMMODITY/CONSIGNMENT */}
          <View>
            <View
              style={[
                styles.tableRow,
                styles.tableCell,
                styles.centerDiv,
                styles.boldText,
              ]}
            >
              <Text>DESCRIPTION OF COMMODITY/CONSIGNMENT</Text>
            </View>

            {/* checkbox */}
            <View
              style={[
                styles.tableRow,
                styles.tableCell,
                styles.boldText,
                { gap: 90, paddingLeft: 20, height: 18, borderBottom: 0 },
              ]}
            >
              {staticData.DESCRIPTION_OF_COMMODITY_CONSIGNMENT.map(
                (desc_of_commodity) => (
                  <Checkbox
                    label={desc_of_commodity.label}
                    checked={desc_of_commodity.checked}
                  />
                ),
              )}
            </View>
          </View>
        </View>

        {/* Main PRODUCT table */}
        <View
          style={[
            {
              borderTop: 1,
              borderBottom: 1,
              // minHeight: productTableHeight,
              display: "flex",
              flexDirection: "column",
            },
          ]}
        >
          {/* Table header*/}
          <View style={[styles.tableRow, styles.boldText, { borderLeft: 1 }]}>
            <View style={[styles.tableCell, styles.centerDiv, { width: "5%" }]}>
              <Text>No</Text>
            </View>
            <View
              style={[styles.tableCell, styles.centerDiv, { width: "15%" }]}
            >
              <Text>Common Name/Scientific Name</Text>
            </View>
            <View
              style={[styles.tableCell, styles.centerDiv, { width: "10%" }]}
            >
              <Text>HS Code</Text>
            </View>
            <View
              style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
            >
              <Text>Quantity/Packaging</Text>
            </View>
            <View
              style={[styles.tableCell, styles.centerDiv, { width: "30%" }]}
            >
              <Text>
                Certificate of Analysis/Health Certificate's Reference Number
                and issued date*)
              </Text>
            </View>
            <View
              style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
            >
              <Text>Testing Laboratory/NFSCA Body*)</Text>
            </View>
          </View>

          {/*Table Rows */}
          {salesInvoiceData?.sales_invoice_products?.map(
            (product: any, index: any) => (
              <View
                key={`product_row_${index}`}
                style={[
                  styles.tableRow,
                  styles.boldText,
                  {
                    borderLeft: 1,
                    minHeight: 40,
                    borderBottom:
                      index ===
                      salesInvoiceData.sales_invoice_products.length - 1
                        ? 0
                        : 1,
                  },
                ]}
                wrap={false}
              >
                {/* Sr No */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "5%" }]}
                >
                  <Text>{index + 1}</Text>
                </View>

                {/* Product Name */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "15%" }]}
                >
                  <Text>{stringToUpperCase(product?.product?.name)}</Text>
                </View>

                {/* HS Code */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "10%" }]}
                >
                  <Text>{product?.product?.hsn_no}</Text>
                </View>

                {/* Quantity / Packing */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
                >
                  <Text>
                    {formatAmount(Number(product.quantity) * 1000, 3)} NET KG
                    {product?.no_of_bags
                      ? ` / ${product.no_of_bags} ${
                          product?.packing_type?.name
                            ? stringToUpperCase(product.packing_type.name)
                            : "BAGS"
                        }`
                      : ""}
                  </Text>
                </View>

                {/* Certificate Ref */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "30%" }]}
                >
                  <Text>
                    {stringToUpperCase(
                      documentInputFields?.certificate_reference_no,
                    )}{" "}
                    ({formatDate(documentInputFields?.certificate_issue_date)})
                  </Text>
                </View>

                {/* Authority */}
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
                >
                  <Text>
                    {stringToUpperCase(documentInputFields?.nfsca_body)}
                  </Text>
                </View>
              </View>
            ),
          )}

          {/* {salesInvoiceData?.sales_invoice_products.map(
            (salesInvoiceProduct: any, index: number) => (
              <View
                style={[
                  styles.tableRow,
                  styles.boldText,
                  {
                    borderLeft: 1,
                    minHeight: 60,
                    borderBottom: index + 1 === productDataLength ? 0 : 1,
                  },
                ]}
                key={`product_row_${index + 1}`}
                wrap={false}
              >
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "5%" }]}
                >
                  <Text>{index + 1}</Text>
                </View>
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "15%" }]}
                >
                  <Text>{salesInvoiceProduct.product.name}</Text>
                </View>
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "10%" }]}
                >
                  <Text>{salesInvoiceProduct.product.hsn_no}</Text>
                </View>
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
                >
                  <Text>
                    {formatAmount(salesInvoiceProduct.quantity * 1000, 3)} net
                    KG
                    {salesInvoiceProduct?.no_of_bags
                      ? `/${salesInvoiceProduct.no_of_bags} ${
                          salesInvoiceProduct?.packing_type?.name
                            ? salesInvoiceProduct.packing_type.name
                            : "BG"
                        }`
                      : ""}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCell,
                    styles.centerDiv,
                    { flexDirection: "column", width: "30%" },
                  ]}
                >
                  {staticData.PRODUCT_ROW_STATIC_DATA.certificate_no.map(
                    (certificate_no) => (
                      <Text>{certificate_no}</Text>
                    )
                  )}
                  <Text>
                    {staticData.PRODUCT_ROW_STATIC_DATA.certificate_issue_date}
                  </Text>
                </View>
                <View
                  style={[styles.tableCell, styles.centerDiv, { width: "20%" }]}
                >
                  <Text>
                    {staticData.PRODUCT_ROW_STATIC_DATA.Testing_laboratory}
                  </Text>
                </View>
              </View>
            )
          )} */}

          {/* Empty row */}

          <View
            style={[
              styles.tableRow,
              { height: emptyRowHeight, borderLeft: 1, borderBottom: 0 },
            ]}
          >
            <View style={[styles.tableCell, { width: "5%" }]}></View>
            <View style={[styles.tableCell, { width: "15%" }]}></View>
            <View style={[styles.tableCell, { width: "10%" }]}></View>
            <View style={[styles.tableCell, { width: "20%" }]}></View>
            <View
              style={[
                styles.tableCell,
                { flexDirection: "column", width: "30%" },
              ]}
            ></View>
            <View style={[styles.tableCell, { width: "20%" }]}></View>
          </View>
        </View>

        <View style={[styles.table, { borderTop: 0 }]}>
          {/* EXPORT PURPOSE & DEGREE OF PROCESSING*/}
          <View wrap={false}>
            <View style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "30%" }]}>
                EXPORT PURPOSE
              </Text>
              <Text style={[styles.tableCell, { width: "70%" }]}>
                {staticData.EXPORT_PURPOSE}
              </Text>
            </View>

            <View style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "30%" }]}>
                DEGREE OF PROCESSING
              </Text>
              <View
                style={[
                  styles.tableCell,
                  {
                    width: "70%",
                    paddingRight: 7,
                    columnGap: 30,
                    flexWrap: "wrap",
                  },
                ]}
              >
                {staticData.DEGREE_OF_PROCESSING.map((obj) => (
                  <Checkbox label={obj.label} checked={obj.checked} />
                ))}
              </View>
            </View>
          </View>

          {/* HEALTH/SANITARY/PHYTOSANITARY CERTIFICATE */}
          <View wrap={false}>
            <View
              style={[
                styles.tableRow,
                styles.tableCell,
                styles.centerDiv,
                styles.boldText,
              ]}
            >
              <Text>HEALTH/SANITARY/PHYTOSANITARY CERTIFICATE*</Text>
            </View>

            {/* Header */}
            <View style={[styles.tableRow, styles.boldText]}>
              <View
                style={[styles.tableCell, styles.centerDiv, { width: "5%" }]}
              >
                <Text>No</Text>
              </View>
              <View
                style={[styles.tableCell, styles.centerDiv, { width: "37%" }]}
              >
                <Text>Reference Number</Text>
              </View>
              <View
                style={[styles.tableCell, styles.centerDiv, { width: "33%" }]}
              >
                <Text>place of Issue</Text>
              </View>
              <View
                style={[styles.tableCell, styles.centerDiv, { width: "25%" }]}
              >
                <Text>Date of Issue</Text>
              </View>
            </View>

            {/* Table Rows */}
            <View style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "5%" }]}>1</Text>
              <Text style={[styles.tableCell, { width: "37%" }]}>
                {stringToUpperCase(documentInputFields?.reference_no)}
              </Text>
              <Text style={[styles.tableCell, { width: "33%" }]}>
                {stringToUpperCase(documentInputFields?.place_of_issue)}
              </Text>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                {formatDate(documentInputFields?.date_of_issue)}
              </Text>
            </View>
          </View>

          {/* PORT details table*/}
          <View>
            {/* Row-1 */}
            <View wrap={false} style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Means of conveyance
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {salesInvoiceData?.pre_carriage_by}
              </Text>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Voyage/Flight number
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {salesInvoiceData?.vessel_no}
              </Text>
            </View>
            {/* Row-2 */}
            <View wrap={false} style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Port of loading
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {stringToUpperCase(salesInvoiceData?.port_of_loading_code)}
              </Text>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Date of loading
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {formatDate(salesInvoiceData?.etd)}
              </Text>
            </View>
            {/* Row-3 */}
            <View wrap={false} style={[styles.tableRow]}>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Place of destination
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {salesInvoiceData?.destination_port
                  ? stringToUpperCase(salesInvoiceData?.destination_port)
                  : "-"}
              </Text>
              <Text style={[styles.tableCell, { width: "25%" }]}>
                Date of estimated arrival
              </Text>
              <Text
                style={[styles.tableCell, styles.boldText, { width: "25%" }]}
              >
                {formatDate(salesInvoiceData?.eta)}
              </Text>
            </View>
          </View>

          {/* Additional information */}
          <View
            wrap={false}
            style={[
              styles.tableRow,
              styles.tableCell,
              { flexDirection: "column" },
            ]}
          >
            <Text>Additional information:</Text>
            <View style={{ flexDirection: "column", width: "100%" }}>
              <Text>Container's identification Number*: </Text>
              <Text
                style={[
                  styles.boldText,
                  {
                    width: "100%",
                    flexWrap: "wrap",
                    lineHeight: 1.4,
                    marginTop: 2,
                  },
                ]}
              >
                {salesInvoiceData?.containers?.map(
                  (container: any, index: number) =>
                    `${container.container_no}${
                      index + 1 < salesInvoiceData?.containers?.length
                        ? "; "
                        : ""
                    }`,
                )}
              </Text>
            </View>
            <Text style={{ marginTop: 5 }}>Other (specify):</Text>
          </View>

          {/* Footer */}
          <View
            wrap={false}
            style={[
              styles.tableRow,
              styles.tableCell,
              styles.boldText,
              {
                justifyContent: "space-between",
                height: 150,
                paddingHorizontal: 20,
              },
            ]}
          >
            <View style={[styles.centerDiv, { flexDirection: "column" }]}>
              <View style={[styles.logo]}></View>
              <Text>Qr Code</Text>
            </View>
            <View
              style={[
                styles.centerDiv,
                {
                  justifyContent: "flex-start",
                  paddingTop: 10,
                },
              ]}
            >
              <Text>
                Place:{" "}
                {stringToUpperCase(documentInputFields?.place_of_applicant)}
              </Text>
              <Text>
                Date: {formatDate(documentInputFields?.date_of_applicant)}
              </Text>
            </View>
            <View style={[styles.centerDiv, { flexDirection: "column" }]}>
              <Text>Applicant</Text>
              {includeSignature && settingsData?.invoice_digital_signature ? (
                <Image
                  src={settingsData.invoice_digital_signature}
                  style={[styles.logo, { border: 0 }]}
                />
              ) : (
                <View style={styles.logo}></View>
              )}
              <Text>({settingsData?.name_of_proprietor})</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PriorNoticeInvoice;
